import { format } from "date-fns";

import { useI18n } from "@/contexts/i18nContext";
import { Episode } from "@/types/Episode";
import { Mention } from "@/types/Mention";
import { cx, isSourceWithTitle } from "@/utils";
import Typography from "@components/Typography";

import { getShowName } from "../Episode/utils/getShowName";
import SourceDisplay from "../SourceDisplay";

import styles from "./ListMention.module.css";

type ListMentionProps = {
  className?: string;
  children?: React.ReactNode;
} & Pick<Mention, "text" | "source" | "channel_name" | "channel_name"> &
  Pick<Episode, "published_date" | "title"> & {
    episode_public_url?: string;
    external_source?: string;
  };

/**
 * A slimmed down mention, ment to be used in lists wher not that much context is needed.
 * - not playable
 * - Meant to be action-heavy.
 * - Actions are passed in as children.
 */

const ListMention: React.FunctionComponent<ListMentionProps> = ({
  className,
  channel_name,
  title,
  source,
  external_source,
  published_date,
  text,
  episode_public_url,
  children,
}) => {
  const { locale } = useI18n();

  return (
    <div className={cx(styles.root, className)}>
      <Typography className={styles.header} margin="none" variant="body-2">
        <SourceDisplay
          className={styles.source}
          title={title}
          noIcon
          external_source={external_source}
          source={source}
        />{" "}
        {format(published_date, "PP", {
          locale,
        })}
      </Typography>
      <a
        // Not giving an a-tag a href simply makes it unclickable.
        {...(episode_public_url ? { href: episode_public_url } : {})}
        target="_blank"
        rel="noreferrer"
      >
        <Typography variant="body-2" margin="none" className={styles.title}>
          {getShowName({ source, show: channel_name })}
          {isSourceWithTitle(source) && (
            <span dangerouslySetInnerHTML={{ __html: ` : ${title}` }} />
          )}
        </Typography>
      </a>
      <Typography
        variant="body-2"
        className="highlight-match"
        dangerouslySetInnerHTML={{ __html: text }}
      />
      {children && <div className={styles.actions}>{children}</div>}
    </div>
  );
};

export default ListMention;
